<script setup lang="ts">
import {
  alertController,
  createAnimation,
  IonContent,
  IonPage,
  IonSpinner,
  onIonViewDidEnter,
  useIonRouter,
} from '@ionic/vue';
import { useRoute } from 'vue-router';
import InputBox from '@/components/forms/InputBox.vue';
import PrimaryBlockButton from '@/components/forms/PrimaryBlockButton.vue';
import { useFormControl } from '@/composables/use-form-control';
import { useAuth } from '@/stores/auth.store';
import { LoginResponse } from 'common';
import { onMounted, ref } from 'vue';
import TermsAndConditionsModal from '@/components/information/TermsAndConditionsModal.vue';
import ForgotPasswordModal from '@/components/user-account/ForgotPasswordModal.vue';
import CreateAccountModal from '@/components/user-account/CreateAccountModal.vue';
import { useBiometrics } from '@/stores/biometrics.store';
import VersionButton from '@/components/information/VersionButton.vue';
import { useToast } from '@/composables/use-toast';
import { UserService } from '@/http-services/user.service';

const auth = useAuth();
const biometrics = useBiometrics();
const emailOrUsername = useFormControl(() => biometrics.cachedUsername || '');
const userService = new UserService();
const password = useFormControl(() => '');
const router = useIonRouter();
const route = useRoute();
const animateLogin = ref(false);
const animateLogo = ref(false);
const { errorToast, successToast } = useToast();

onMounted(() => {
  setTimeout(() => {
    animateLogo.value = true;
  }, 100);
  setTimeout(() => {
    animateLogin.value = true;
  }, 800);
});

onIonViewDidEnter(() => {
  emailOrUsername.value = route.query.email || biometrics.cachedUsername || '';
  password.value = '';
});

async function loginClicked() {
  if (auth.state === 'AUTHENTICATING') return;
  if (emailOrUsername.value && password.value) {
    const response = await auth.login(emailOrUsername.value, password.value);
    await onLoginResponse(response);
  }
}

async function onLoginResponse(response: LoginResponse) {
  if (response.success) {
    password.value = '';
    router.replace('/', () => createAnimation());
  } else {
    errorToast(response.message || 'Login failed');
    auth.state = 'UNAUTHENTICATED';
    if (response.message === 'Account not activated yet') {
      await onAccountNotActivatedYet();
    }
  }
}

async function onAccountNotActivatedYet() {
  const a = await alertController.create({
    header: 'Account not activated',
    message:
      'Email verification is required to activate your account. You should have received an email with a verification link when you created your account.',
    buttons: [
      { text: 'Ok', role: 'cancel' },
      { text: 'Resend Email', role: 'resend' },
    ],
  });
  await a.present();
  const { role } = await a.onDidDismiss();
  if (role === 'resend') {
    const { message, success } = await userService.resendActivationEmail(emailOrUsername.value);
    if (success) {
      successToast(message);
    } else {
      errorToast(message || 'Failed to resend activation email');
    }
  }
}

async function biometricLogin() {
  const response = await auth.biometricLogin();
  await onLoginResponse(response);
}
</script>
<template>
  <IonPage>
    <IonContent class="ion-padding" :scroll-y="false" :scroll-x="false">
      <div class="h-full px-4 pb-4 flex flex-col justify-center items-center">
        <div
          class="h-1/3 pl-4 logo-container flex flex-col justify-end"
          :style="{ filter: animateLogo ? 'blur(0)' : 'blur(10px)' }"
        >
          <img class="w-[250px] mx-auto" src="@/assets/next-century-logo-white.svg" />
        </div>
        <div
          :style="{ opacity: animateLogin ? 1 : 0 }"
          class="main-container flex flex-col h-2/3 items-center gap-6 mt-6"
        >
          <form
            id="login-form"
            data-test-id="login-form"
            @submit.prevent="loginClicked()"
            class="form-background w-full max-w-[400px] flex flex-col space-y-4 p-6 main-container"
          >
            <InputBox
              v-model="emailOrUsername.value"
              icon="icon-user"
              placeholder="Username or Email"
              data-test-id="username"
              inputmode="email"
              exclude-borders
            ></InputBox>
            <InputBox
              v-model="password.value"
              icon="icon-key"
              type="password"
              placeholder="Password"
              data-test-id="password"
              exclude-borders
            ></InputBox>
            <div class="flex">
              <div class="flex-1">
                <PrimaryBlockButton type="submit" :disabled="auth.state === 'AUTHENTICATING'">
                  <Transition name="fade" mode="out-in">
                    <IonSpinner
                      class="size-6"
                      name="dots"
                      color="white"
                      v-if="auth.state === 'AUTHENTICATING'"
                    ></IonSpinner>
                    <span v-else>Login</span>
                  </Transition>
                </PrimaryBlockButton>
              </div>
              <div>
                <PrimaryBlockButton size="large" @click="biometricLogin()" :disabled="!biometrics.isAvailable">
                  <i slot="icon-only" :class="biometrics.isFaceID ? 'icon-face-id' : 'icon-touch-id'"></i>
                </PrimaryBlockButton>
              </div>
            </div>
            <div class="flex flex-row justify-around items-center">
              <CreateAccountModal />
              <span class="border-r-[1px] border-white/70 h-3 relative"></span>
              <ForgotPasswordModal />
            </div>
          </form>
          <TermsAndConditionsModal></TermsAndConditionsModal>
          <VersionButton class="mt-auto"></VersionButton>
        </div>
      </div>
    </IonContent>
  </IonPage>
</template>

<style scoped>
ion-content {
  --background: linear-gradient(45deg, #004f85 0%, #00acd2 100%);
}

ion-content::part(scroll) {
  padding-top: var(--ion-safe-area-top, 0);
}

.form-background {
  background: rgba(0, 0, 0, 0.1);
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.logo-container {
  transition: filter 1s ease;
  padding: 20px;
  position: relative;
  left: 10px;
}

.main-container {
  transition: opacity 1s ease;
}
</style>
